import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  CircularProgress, 
  Divider, 
  Chip,
  Paper,
  Grid2 as Grid,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  Tooltip,
  Stack
} from '@mui/material'; 
import { 
  BarChart, 
  InfoOutlined, 
  Warning 
} from '@mui/icons-material';
import axios from 'axios';

// Component to display a usage progress bar with label
const UsageProgressBar = ({ label, current, max, color }) => {
  const value = max > 0 ? Math.min(100, (current / max) * 100) : 0;
  const formattedValue = value.toFixed(1);
  
  return (
    <Box sx={{ mb: 1.5 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
        <Typography variant="body2" color="text.secondary">
          {label}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {current} / {max === Infinity ? '∞' : max} ({formattedValue}%)
        </Typography>
      </Box>
      <LinearProgress 
        variant="determinate" 
        value={value} 
        color={color || "primary"} 
        sx={{ height: 6, borderRadius: 1 }} 
      />
    </Box>
  );
};

function ApiKeyUsage({ apiKeyId }) {
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  useEffect(() => {
    const fetchUsageData = async () => {
        try {
          setLoading(true);
          const response = await axios.get(`/api/usage/api-key/${apiKeyId}`);
          setUsage(response.data.data);
          setError(null);
        } catch (err) {
          console.error('Error fetching API key usage:', err);
          setError('Failed to load usage statistics');
        } finally {
          setLoading(false);
        }
      };
      
    if (apiKeyId) {
      fetchUsageData();
    }
  }, [apiKeyId]);
  

  
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }
  
  if (error) {
    return (
      <Box sx={{ p: 2, color: 'error.main' }}>
        <Typography variant="body2">{error}</Typography>
      </Box>
    );
  }
  
  if (!usage) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="body2">No usage data available</Typography>
      </Box>
    );
  }
  
  const { 
    retrievalCount,
    submissionCount,
    amendmentCount,
    retractionCount,
    echoCount,
    hscodesCount,
    totalCount 
  } = usage.usage;

  // Determine color based on usage percentage
  const getUsageColor = (percentage) => {
    if (percentage >= 90) return 'error';
    if (percentage >= 70) return 'warning';
    return 'primary';
  };
  
  const usageColor = getUsageColor(usage.usagePercentage);
  const formattedLastUsed = usage.apiKey.lastUsed 
    ? new Date(usage.apiKey.lastUsed).toLocaleString() 
    : 'Never';
  
  return (
    <Paper sx={{ width: '100%', bgcolor: 'background.paper' }}>
      <Stack spacing={2} sx={{ p: 3 }}>
        {/* Key Details Section */}
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={4}>
          {/* Key Performance */}
          <Stack sx={{ flex: 1 }}>
            <Typography variant="subtitle1" gutterBottom fontWeight="medium">
              Key Performance
            </Typography>
            
            <UsageProgressBar
              label="Total API Calls"
              current={totalCount}
              max={usage.tier.limit}
              color={usageColor}
            />
            
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Last Used: {formattedLastUsed}
            </Typography>
            
            {usage.usagePercentage > 70 && (
              <Box sx={{ display: 'flex', alignItems: 'center', mt: 1, bgcolor: 'warning.light', p: 1, borderRadius: 1 }}>
                <Warning color="warning" fontSize="small" sx={{ mr: 1 }} />
                <Typography variant="body2" color="warning.dark">
                  Using {usage.usagePercentage.toFixed(1)}% of limit
                </Typography>
              </Box>
            )}
          </Stack>
          
          {/* Period Information */}
          <Stack sx={{ flex: 1 }}>
            <Typography variant="subtitle1" gutterBottom fontWeight="medium">
              Period Information
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Year: {usage.period.year}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Month: {usage.period.month}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Tier: {usage.tier.name.charAt(0).toUpperCase() + usage.tier.name.slice(1)}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Monthly Limit: {usage.tier.limit} API calls
            </Typography>
          </Stack>
        </Stack>
        
        <Divider />
        
        {/* Operation Type Breakdown */}
        <Box>
          <Typography variant="subtitle1" gutterBottom fontWeight="medium">
            Operation Type Breakdown
          </Typography>
          
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={4}>
            <Stack sx={{ flex: 1 }}>
              <UsageProgressBar
                label="Retrievals"
                current={retrievalCount}
                max={usage.tier.limit}
              />
              <UsageProgressBar
                label="Submissions"
                current={submissionCount}
                max={usage.tier.limit}
              />
            </Stack>
            
            <Stack sx={{ flex: 1 }}>
              <UsageProgressBar
                label="Amendments"
                current={amendmentCount}
                max={usage.tier.limit}
              />
              <UsageProgressBar
                label="Retractions"
                current={retractionCount}
                max={usage.tier.limit}
              />
            </Stack>
          </Stack>
        </Box>
        
        {/* Non-billable Operations */}
        <Box>
          <Typography variant="subtitle1" gutterBottom fontWeight="medium">
            Non-billable Operations
          </Typography>
          
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={4}>
            <Stack sx={{ flex: 1 }}>
              <UsageProgressBar
                label="Echo Requests"
                current={echoCount}
                max={Infinity}
              />
            </Stack>
            <Stack sx={{ flex: 1 }}>
              <UsageProgressBar
                label="HS Codes Requests"
                current={hscodesCount}
                max={Infinity}
              />
            </Stack>
          </Stack>
        </Box>
      </Stack>
    </Paper>
  );
}

export default ApiKeyUsage; 